//@ts-check
import { isSafari } from "common/utils/browser-detection";
import debug from "debug";
import $ from "jquery";
import { applySampleFormatting } from "./sample/applySampleFormatting";
import { correctionPixels } from "./type-tester/text-editor/ascender-padding";
import { determineLineHeightGapCorrection } from "./type-tester/text-editor/line-height-gap-correction";
import { TypeTester } from "./type-tester/TypeTester";
import {
  getSituationalMaxScaledFontSize,
  maxScaledFontSize,
  minScaledFontSize,
} from "./type-tester/formatting-tools/font-size-basics";
import { max, min } from "common/utils/number-limits";
import { logMaxFontSize } from "../utils/debug-loggers";
import { logGroup } from "../utils/logging";

let lastId = 0;

/**
 * Scales font samples according to screen width.
 * It also calculates and applies other properties like max-height, columns, ascender/descender correction.
 *
 * Usage:
 *
 *   FontScaler.startAll(".font_style_sample", { scaleUp: true });
 *
 * Side effects:
 * - data attribute 'fontSize' - this will be used as the basis, which means the font size meant for the 1000px width; if missing, it will be initialized from the actual size on the first call
 *
 * Used by:
 * - TypeTester, via buildFontScalerForTypeTester (element is renderedTextElement)
 * - Typefaces Overview page (for scaling down only)
 * - custom scripts (for .FontWindow, #FontWindow, #beausitePlan)
 *
 */
class FontScaler {
  /**
   * @typedef {import("./type-tester/types").TypeTesterFontSettings} FontSettings
   */
  /**
   * @typedef {Object} Config
   * @prop {number} [upSpeed] The speed of scaling up
   * @prop {number} [downSpeed] The speed of scaling down
   * @prop {number} [maxScale] The maximum scaling factor
   * @prop {number} [minScale] The minimum scaling factor
   * @prop {number} [maxFontSize] The maximum font size
   * @prop {number} [minFontSize] The minimum font size
   * @prop {number} [paddingX] The padding to subtract from the width when calculating the ratio
   * @prop {boolean} [scaleUp] Whether to scale up
   * @prop {function} [onBeforeResize] Function to call before resizing an element; called with a JQuery element
   * @prop {function} [onAfterResize] Function to call after resizing an element; called with a JQuery element
   * @prop {number} [maxHeight] The maximum height of the element
   * @prop {null | "text"} [maxHeightStrategy] The strategy to use for max-height; e.g 'text'
   * @prop {number | null} [initialTextLength] The initial text length to use when calculating the character limit for text shortening
   * @prop {number} [columnWidth] The max width of a column in em
   * @prop {boolean} [automaticColumnsEnabled] Whether to automatically calculate the number of columns (default: true if columnWidth is set); this can be used to disable the automatic calculation, e.g. if the number of columns has been selected by the user
   * @prop {HTMLElement} [dimensionElement] The element to use for the width calculation; defaults to window width - paddingX
   * @prop {string} [loggingId] The id to use for logging
   * @prop {Callbacks} [callbacks] Callbacks
   */

  /**
   * @typedef {Object} Callbacks
   * @prop {function} [applyColumns] Function to call to apply the number of columns
   * @prop {function} [applyMaxHeight] Function to call to apply the max height
   * @prop {function} [applyAscendersDescenderCorrection] Function to call to apply the ascender and descender padding
   * @prop {function} [onResize] Function to call when new dimensions are applied
   */

  /**
   * @typedef {Object} Inputs
   * @prop {number} fontSize The unscaled font size
   * @prop {number} lineHeight The line height (e.g. 1, 1.2)
   * @prop {number} availableWidth The width available to display the sample
   * @prop {number | null} columns
   * @prop {FontSettings} fontSettings
   * @prop {number} textLength
   */

  /**
   * @typedef {import("./sample/types").SampleFormattingResults} Results
   */

  /** @type {Config} */
  config;

  isActive = false;

  /**
   * @type {FontSettings?}
   * Set when receiving a change event from TypeTester (see TextEditor mainFormat)
   */
  fontSettings;

  columns;
  textLength;

  /**
   * @param {HTMLElement} element
   * @param {Config} config
   */
  constructor(element, config = {}) {
    if (!element) {
      throw new Error("font-scaler: no element provided");
    }

    const debugId = ++lastId;
    this.element = element;
    this.config = config;

    if (element.dataset.scaleDownSpeed) {
      this.config.downSpeed = parseFloat(element.dataset.scaleDownSpeed);
    }

    this.log = debug(`font-scaler:${config.loggingId ?? debugId}:FontScaler`);
    this.log("initialized", config);
  }

  /**
   * @param {HTMLElement} element
   * @param {Config} config
   */
  static start(element, config) {
    const fontScaler = new FontScaler(element, config);
    fontScaler.start();
    return fontScaler;
  }

  /**
   * Start scaling all elements with the class .rendered_text within the provided selector.
   *
   * @param {string} selector CSS selector used as a scope, we'll look for .rendered_text within this.
   * @param {Config} options optional
   */
  static startAll(selector, options) {
    $(`${selector ?? ""} .rendered_text`).each((index, element) => {
      const fontScaler = new FontScaler(element, options);
      fontScaler.start();
    });
  }

  start() {
    logGroup("font-scaler: start 🚀", () => {
      this.saveUnscaledFontSizeIfMissing();

      this.lineHeight = null;

      if (!this.element.dataset.unscaledFontSize) {
        throw new Error("element.dataset.unscaledFontSize is missing");
      }

      this.unscaledFontSize = this.element.dataset.unscaledFontSize;

      this.listenToWindowResize();
      this.listenToTypeTesterChanges();

      this.isActive = true;

      this.log("initial resize");
      this.resizeIfActive();
    });
  }

  saveUnscaledFontSizeIfMissing() {
    // Remember the font size on the first call
    if (!this.element.dataset.unscaledFontSize) {
      this.element.dataset.unscaledFontSize = parseFloat(
        this.element.style.fontSize
      ).toString();
    }
  }

  listenToWindowResize() {
    window.addEventListener("resize", () => {
      this.log("window resized");
      this.resizeIfActive();
    });
  }

  listenToTypeTesterChanges() {
    // Listen to Type-Tester changes, so we have the line-height for the max-height calculation
    this.element.addEventListener("change", (event) => {});
  }

  resizeIfActive() {
    if (!this.isActive) return;
    this.resize();
  }

  resize() {
    logGroup("font-scaler resize", () => {
      this.resizeInner();
    });
  }

  resizeInner() {
    this.beforeResizeCallback();

    const availableWidth =
      this.config.dimensionElement?.clientWidth ??
      window.innerWidth - (this.config.paddingX ?? 30);

    /** @type {Inputs} */
    const inputs = {
      fontSize: parseFloat(this.unscaledFontSize),
      lineHeight: this.lineHeight ?? 1,
      availableWidth,
      columns: this.columns,
      fontSettings: this.fontSettings,
      textLength: this.textLength,
    };

    this.log("inputs", inputs);

    /** @type {Results} */
    const results = calculate(this.config, inputs, this.log);

    this.log("results", results);

    this.applyResults(results);

    // Save scaling factor so that other components (e.g. type-tester) may use it
    this.element.dataset.scalingFactor = results.scalingFactor.toString();

    this.log("resize", this.config, inputs, results);

    if (this.config.callbacks?.onResize) {
      this.config.callbacks.onResize(this);
    }

    this.afterResizeCallback();
  }

  beforeResizeCallback() {
    if (typeof this.config.onBeforeResize === "function") {
      this.config.onBeforeResize($(this.element));
    }
  }

  afterResizeCallback() {
    if (typeof this.config.onAfterResize === "function") {
      this.config.onAfterResize($(this.element));
    }
  }

  /**
   * @param {Results} results
   */
  applyResults(results) {
    const typeTester = TypeTester.getInstanceByElement(
      this.element.closest(".type-tester")
    );
    /** @type {import("./sample/types").SampleEnvironment} */
    const sampleEnvironment = {
      renderedTextElement: this.element,
      editorElement: this.element.querySelector(".ql-editor"),
      fontSettings: this.fontSettings,
      typeTester: typeTester,
      settings: {
        maxHeightStrategy: this.config.maxHeightStrategy,
      },
    };

    applySampleFormatting(sampleEnvironment, results);
  }

  set upSpeed(value) {
    if (value !== this.config.upSpeed) {
      this.config.upSpeed = value;
      this.log("upSpeed changed", value);
      this.resizeIfActive();
    }
  }

  set downSpeed(value) {
    if (value !== this.config.downSpeed) {
      this.config.downSpeed = value;
      this.log("downSpeed changed", value);
      this.resizeIfActive();
    }
  }

  set maxScale(value) {
    if (value !== this.config.maxScale) {
      this.config.maxScale = value;
      this.log("maxScale changed", value);
      this.resizeIfActive();
    }
  }

  set minScale(value) {
    if (value != this.config.minScale) {
      this.config.minScale = value;
      this.log("minScale changed", value);
      this.resizeIfActive();
    }
  }

  set maxFontSize(value) {
    if (value != this.config.maxFontSize) {
      this.config.maxFontSize = value;
      this.log("maxFontSize changed", value);
      this.resizeIfActive();
    }
  }

  set minFontSize(value) {
    if (value != this.config.minFontSize) {
      this.config.minFontSize = value;
      this.log("minFontSize changed", value);
      this.resizeIfActive();
    }
  }

  set columnWidth(value) {
    if (value != this.config.columnWidth) {
      this.config.columnWidth = value;
      this.log("columnWidth changed", value);
      this.resizeIfActive();
    }
  }

  set maxHeight(value) {
    if (value != this.config.maxHeight) {
      this.config.maxHeight = value;
      this.log("maxHeight changed", value);
      this.resizeIfActive();
    }
  }

  set automaticColumnsEnabled(value) {
    if (value !== this.config.automaticColumnsEnabled) {
      this.config.automaticColumnsEnabled = value;
      this.log("automaticColumnsEnabled changed", value);
      this.resizeIfActive();
    }
  }

  set maxHeightStrategy(value) {
    if (value !== this.config.maxHeightStrategy) {
      this.config.maxHeightStrategy = value;
      this.log("maxHeightStrategy changed", value);
      this.resizeIfActive();
    }
  }
}

/**
 * Calculate the scaled font size and height.
 *
 * @param {Config} config
 * @param {Inputs} inputs
 * @returns {Results}
 */
function calculate(config, inputs, log) {
  const {
    scaleUp,
    upSpeed,
    downSpeed,
    maxScale,
    minScale,
    maxFontSize,
    minFontSize,
    maxHeight,
    columnWidth,
    automaticColumnsEnabled,
  } = config;
  const {
    fontSize: unscaledFontSize,
    lineHeight,
    availableWidth,
    textLength,
  } = inputs;

  ensureNumber(unscaledFontSize, "unscaledFontSize");

  const referenceWidth = 1000;

  const shouldScale = scaleUp || availableWidth < referenceWidth;

  let scalingFactor = 1;

  if (shouldScale) {
    scalingFactor = availableWidth / referenceWidth;

    const diff = scalingFactor - 1;

    if (diff > 0 && typeof upSpeed === "number") {
      scalingFactor = 1 + (diff * (upSpeed ?? 100)) / 100;
    }

    if (diff < 0 && typeof downSpeed === "number") {
      scalingFactor = 1 + (diff * (downSpeed ?? 100)) / 100;
    }

    if (maxScale) {
      scalingFactor = Math.min(maxScale / 100, scalingFactor);
    }

    if (minScale) {
      scalingFactor = Math.max(minScale / 100, scalingFactor);
    }
  }
  ensureNumber(scalingFactor, "scalingFactor");

  // Calculate scaled font-size
  let scaledFontSize = unscaledFontSize * scalingFactor;

  logMaxFontSize(`scaledFontSize: ${scaledFontSize}`);
  logMaxFontSize(`maxFontSize: ${unscaledFontSize}`);
  logMaxFontSize(`maxScaledFontSize: ${maxScaledFontSize}`);
  scaledFontSize = min(scaledFontSize, maxFontSize, maxScaledFontSize);

  logMaxFontSize(`minFontSize: ${minFontSize}`);
  logMaxFontSize(`minScaledFontSize: ${minScaledFontSize}`);
  scaledFontSize = max(scaledFontSize, minFontSize, minScaledFontSize);
  logMaxFontSize("scaledFontSize:", scaledFontSize);

  ensureNumber(scaledFontSize, "scaledFontSize");

  // ===== Columns =====

  // log(
  //   "config.automaticColumnsEnabled",
  //   config.automaticColumnsEnabled,
  //   columnWidth
  // );

  let columns = inputs.columns ?? null;
  let columnsDeterminedAutomatically = false;
  if (automaticColumnsEnabled !== false) {
    if (typeof columnWidth === "number") {
      // Note: using availableWidth/scaledFontSize instead of 1000/unscaledFontSize because scaledFontSize also takes into account min/max settings

      // availableWidth / unscaledFontSize => how many units fit in availableWidth
      // availableWidth / columnWidth / scaledFontSize => how many columns fit in availableWidth
      columns = Math.ceil(availableWidth / columnWidth / scaledFontSize);
      columns = Math.min(3, Math.max(1, columns));
      columnsDeterminedAutomatically = true;
      logMaxFontSize(
        `automatic columns enabled, columns: ${columns} (availableWidth: ${availableWidth}, columnWidth: ${columnWidth}, scaledFontSize: ${scaledFontSize})`
      );
    }
    // log("columns", columns);
  }

  scaledFontSize = min(
    scaledFontSize,
    getSituationalMaxScaledFontSize({
      columnCount: columns,
      textLength: textLength,
      columnsDeterminedAutomatically: columnsDeterminedAutomatically,
    })
  );
  logMaxFontSize(`scaledFontSize after situational: ${scaledFontSize}`);

  // ====== Line Height Gap Corrections =====
  let lineHeightGapCorrections = { top: 0, bottom: 0 };

  if (inputs.fontSettings) {
    lineHeightGapCorrections = determineLineHeightGapCorrection(
      inputs.fontSettings,
      {
        lineHeightPercent: lineHeight * 100,
        fontSize: scaledFontSize,
        columns,
        browser: { isSafari: isSafari() },
      }
    );
  }

  // Calculate height

  let height = null;
  let lines = null;
  let wrap = true;
  let ascenderCorrection = 0;
  let descenderCorrection = 0;

  if (inputs.fontSettings) {
    ascenderCorrection = correctionPixels(
      inputs.fontSettings.minLineHeightAscender,
      lineHeight * 100,
      scaledFontSize
    );
    descenderCorrection = correctionPixels(
      inputs.fontSettings.minLineHeightDescender,
      lineHeight * 100,
      scaledFontSize
    );
  }

  // ===== Max-Height =====

  if (typeof maxHeight === "number") {
    // Experimentally determined line-height correction for Safari.
    // - if too low, the height of the box will be too small, and the last line will jump to the next column
    const lineHeightCorrection = isSafari() ? 0.975 : 1;

    const effectiveLineHeight =
      scaledFontSize * lineHeight * lineHeightCorrection;

    lines = Math.floor((maxHeight * scalingFactor) / effectiveLineHeight);
    if (lines < 1) {
      lines = 1;
    }

    // Always wrap when multi-column (even if we calculated a single line)
    wrap = lines > 1 || columns > 1;

    const correction = ascenderCorrection + descenderCorrection;

    height = wrap ? Math.ceil(lines * effectiveLineHeight + correction) : null;

    // log(
    //   `- maxHeight = ${maxHeight}\n` +
    //     `  scalingFactor = ${scalingFactor}\n` +
    //     `  scaledFontSize = ${scaledFontSize}\n` +
    //     `  lineHeight = ${lineHeight}\n` +
    //     `  effectiveLineHeight = ${effectiveLineHeight} // scaledFontSize * lineHeight\n` +
    //     `  scalingFactor = ${scalingFactor}\n` +
    //     `  lines = ${lines} // Math.floor((maxHeight * scalingFactor) / effectiveLineHeight)\n` +
    //     `  wrap = ${wrap} // lines > 1\n` +
    //     `  height without correction = ${Math.ceil(
    //       lines * effectiveLineHeight
    //     )}\n` +
    //     `  correction = ${correction}\n` +
    //     `  height = ${height} // Math.ceil(lines * effectiveLineHeight)`
    // );
  }

  return {
    fontSize: unscaledFontSize,
    scalingFactor,
    scaledFontSize,
    height,
    lines,
    columns,
    wrap,
    ascenderCorrection,
    descenderCorrection,
    lineHeightGapCorrections,
    lineHeight,
    columnsDeterminedAutomatically,
  };
}

function ensureNumber(value, name) {
  if (typeof value !== "number" || isNaN(value)) {
    console.error(`${name} is not a number: ${value} (${typeof value})`);
    throw new Error(`${name} is not a number: ${value} (${typeof value})`);
  }
}

// @ts-ignore
window.FontScaler = FontScaler;
